import React from 'react'
import {
    Card, CardContent, CardHeader, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography,
    ToggleButton, ToggleButtonGroup,
} from '@mui/material'
import BarChartIcon from '@mui/icons-material/BarChart'
import TableChartIcon from '@mui/icons-material/TableChart'
import _ from 'lodash'
import { getDiffByIgnoringMidNight, numberPrecision } from '../../../../helpers/UtilityHelper'
import useGetCourier from '../useGetCourier'
import { HorizontalStackedBarChart } from '../../../charts/BarChart'
import getColor from '../../../charts/colors'

const DAY_KEYS = ['p0', 'p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7+']
const DAY_LABELS = {
    p0: 'P2A-0', p1: 'P2A-1', p2: 'P2A-2', p3: 'P2A-3',
    p4: 'P2A-4', p5: 'P2A-5', p6: 'P2A-6', 'p7+': 'P2A-7+',
}

const HEADER_BG = '#FFEB3B'

const headerCellSx = {
    backgroundColor: HEADER_BG,
    fontWeight: 'bold',
    fontSize: '0.75rem',
    border: '1px solid #ccc',
    whiteSpace: 'nowrap',
    padding: '6px 10px',
}

const stickySx = { position: 'sticky', left: 0, zIndex: 2, backgroundColor: HEADER_BG }

function getCellBg(val) {
    if (val >= 30) return '#c8e6c9'
    if (val >= 10) return '#fff9c4'
    if (val >= 1) return '#ffe0b2'
    return 'inherit'
}

function getDayKey(p2aDays) {
    if (p2aDays < 0) return null
    if (p2aDays === 0) return 'p0'
    if (p2aDays === 1) return 'p1'
    if (p2aDays === 2) return 'p2'
    if (p2aDays === 3) return 'p3'
    if (p2aDays === 4) return 'p4'
    if (p2aDays === 5) return 'p5'
    if (p2aDays === 6) return 'p6'
    return 'p7+'
}

export default function P2ADeliverySpeedReport({ shipments = [] }) {
    const [view, setView] = React.useState('table')
    const { data: allCouriers = [] } = useGetCourier()
    const courierMap = React.useMemo(() => _.keyBy(allCouriers, 'id'), [allCouriers])

    const { rows, grandTotals } = React.useMemo(() => {
        const eligible = shipments.filter((s) => {
            const firstAttempt = s.firstDeliveryAttemptTimestamp
            return s.estimatedPickupDate && firstAttempt
        })

        if (eligible.length === 0) return { rows: [], grandTotals: {} }

        const byCourier = _.groupBy(eligible, 'courier')

        const gtCounts = {}
        DAY_KEYS.forEach((k) => (gtCounts[k] = 0))
        let gtTotal = 0

        const rows = Object.entries(byCourier)
            .map(([courierId, items]) => {
                const courier = courierMap[courierId]
                const name = courier ? courier.parent || courier.name : courierId
                const counts = {}
                DAY_KEYS.forEach((k) => (counts[k] = 0))
                let valid = 0

                items.forEach((s) => {
                    const firstAttempt = s.firstDeliveryAttemptTimestamp
                    const days = getDiffByIgnoringMidNight(s.estimatedPickupDate, firstAttempt)
                    const key = getDayKey(days)
                    if (key) {
                        counts[key]++
                        gtCounts[key]++
                        valid++
                        gtTotal++
                    }
                })

                const row = { name, total: valid }
                DAY_KEYS.forEach((k) => {
                    row[`${k}_count`] = counts[k]
                    row[`${k}_pct`] = valid > 0 ? Number(numberPrecision((counts[k] / valid) * 100, 1)) : 0
                })
                return row
            })
            .sort((a, b) => b.total - a.total)

        const grandTotals = { total: gtTotal }
        DAY_KEYS.forEach((k) => {
            grandTotals[`${k}_count`] = gtCounts[k]
            grandTotals[`${k}_pct`] =
                gtTotal > 0 ? Number(numberPrecision((gtCounts[k] / gtTotal) * 100, 1)) : 0
        })

        return { rows, grandTotals }
    }, [shipments, courierMap])

    const chartData = React.useMemo(
        () =>
            rows.slice(0, 15).map((row) => {
                const entry = { name: row.name }
                DAY_KEYS.forEach((k) => {
                    entry[DAY_LABELS[k]] = row[`${k}_pct`]
                })
                return entry
            }),
        [rows]
    )

    const chartOptions = {
        yaxis: 'name',
        yLabel: 'Courier',
        xLabel: '% Shipments',
        stackId: 'stack1',
        domain: [0, 101],
        useSimpleTooltip: true,
        data: DAY_KEYS.map((k, i) => ({
            dataKey: DAY_LABELS[k],
            label: DAY_LABELS[k],
            unit: '%',
            fill: getColor(i + 5),
        })),
    }

    if (shipments.length === 0) return null

    const noData = rows.length === 0

    return (
        <Card elevation={1}>
            <CardHeader
                title='P2A Delivery Speed Report (Courier-wise)'
                subheader='Pickup-to-first-attempt day distribution per courier. P2A-0 = attempted same day as pickup; P2A-1 = 1 day after pickup, etc.'
                action={
                    <ToggleButtonGroup
                        color='primary'
                        size='small'
                        exclusive
                        value={view}
                        onChange={(_e, v) => v && setView(v)}
                    >
                        <ToggleButton value='chart' title='View Chart'>
                            <BarChartIcon fontSize='small' />
                        </ToggleButton>
                        <ToggleButton value='table' title='View Table'>
                            <TableChartIcon fontSize='small' />
                        </ToggleButton>
                    </ToggleButtonGroup>
                }
            />
            <CardContent>
                {noData ? (
                    <Typography variant='body2' color='text.secondary'>
                        No P2A data available.
                    </Typography>
                ) : view === 'chart' ? (
                    <HorizontalStackedBarChart
                        data={chartData}
                        chartOptions={chartOptions}
                        height={Math.max(300, rows.slice(0, 15).length * 30)}
                    />
                ) : (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size='small' sx={{ borderCollapse: 'collapse', minWidth: 600 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...headerCellSx, ...stickySx }}>Courier</TableCell>
                                    {DAY_KEYS.map((k) => (
                                        <TableCell key={k} align='center' sx={headerCellSx}>
                                            {DAY_LABELS[k]}
                                        </TableCell>
                                    ))}
                                    <TableCell align='center' sx={headerCellSx}>
                                        Total
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow key={row.name} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                        <TableCell
                                            sx={{
                                                fontSize: '0.75rem',
                                                border: '1px solid #ccc',
                                                position: 'sticky',
                                                left: 0,
                                                backgroundColor: '#fff',
                                                zIndex: 1,
                                                padding: '5px 10px',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {row.name}
                                        </TableCell>
                                        {DAY_KEYS.map((k) => (
                                            <TableCell
                                                key={k}
                                                align='center'
                                                sx={{
                                                    border: '1px solid #ccc',
                                                    backgroundColor: getCellBg(row[`${k}_pct`]),
                                                    fontSize: '0.75rem',
                                                    padding: '5px 10px',
                                                }}
                                            >
                                                {numberPrecision(row[`${k}_pct`], 1)}%
                                            </TableCell>
                                        ))}
                                        <TableCell
                                            align='center'
                                            sx={{
                                                border: '1px solid #ccc',
                                                fontWeight: 'bold',
                                                fontSize: '0.75rem',
                                                padding: '5px 10px',
                                            }}
                                        >
                                            {row.total}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ backgroundColor: '#212121' }}>
                                    <TableCell
                                        sx={{
                                            color: '#fff',
                                            fontWeight: 'bold',
                                            border: '1px solid #555',
                                            fontSize: '0.75rem',
                                            padding: '5px 10px',
                                            position: 'sticky',
                                            left: 0,
                                            backgroundColor: '#212121',
                                            zIndex: 1,
                                        }}
                                    >
                                        Grand Total
                                    </TableCell>
                                    {DAY_KEYS.map((k) => (
                                        <TableCell
                                            key={k}
                                            align='center'
                                            sx={{
                                                color: '#fff',
                                                fontWeight: 'bold',
                                                border: '1px solid #555',
                                                fontSize: '0.75rem',
                                                padding: '5px 10px',
                                            }}
                                        >
                                            {numberPrecision(grandTotals[`${k}_pct`], 1)}%
                                        </TableCell>
                                    ))}
                                    <TableCell
                                        align='center'
                                        sx={{
                                            color: '#fff',
                                            fontWeight: 'bold',
                                            border: '1px solid #555',
                                            fontSize: '0.75rem',
                                            padding: '5px 10px',
                                        }}
                                    >
                                        {grandTotals.total}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    )
}
